import React, { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Table } from '@landr/maestro';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import {
    SamplesTableActions,
    SamplesTableColumns,
    StreamEntityType,
} from 'types/enums';
import useDetectTargetIsInsideViewport from 'hooks/useDetectTargetIsInsideViewport/useDetectTargetIsInsideViewport';
import { PlayerProvider } from 'contexts/PlayerContext/PlayerContext';
import { AudioConfigParams } from 'types/params';
import { Sample } from 'types/generated/graphql';
import { TableEventSubscriptionTypes } from 'helpers/tableEvent/tableEvent';
import { SamplesTableActionsGroup } from '../SamplesTableActionsGroup';
import { SamplesTableActionsGroupSkeletons } from '../SamplesTableActionsGroup/SamplesTableActionsGroup.skeletons';
import {
    waveformCell,
    nameCell,
    informationsCell,
    actionsGroupCellStyles,
    playButtonCell,
} from '../SamplesTable.styled';
import SamplesTableCheckbox from './components/SamplesTableCheckbox';
import SamplesCell from './components/SamplesCell/SamplesCell';
import {
    SamplesTablePackIcon,
    SamplesTablePackIconSkeleton,
} from './components/SamplesTablePackIcon';
import {
    SamplesTableWaveform,
    SamplesTableWaveformSkeleton,
} from './components/SamplesTablePlayer/SamplesTableWaveform';
import {
    SamplesTableTypeIcon,
    SamplesTableTypeIconSkeleton,
} from './components/SamplesTableTypeIcon/SamplesTableTypeIcon';
import {
    SamplesTableName,
    SamplesTableNameSkeleton,
} from './components/SamplesTableName';
import {
    SamplesTableKey,
    SamplesTableKeySkeleton,
} from './components/SamplesTableKey';
import {
    SamplesTableBpm,
    SamplesTableBpmSkeleton,
} from './components/SamplesTableBpm';
import { SamplesTableRowObserver } from './components/SamplesTableRowObserver/SamplesTableRowObserver';
import { SamplesTableCheckboxSkeleton } from './components/SamplesTableCheckbox/SamplesTableCheckbox';
import {
    SamplesTablePlayButton,
    SamplesTablePlayButtonSkeleton,
} from './components/SamplesTablePlayer/SamplesTablePlayButton';
import {
    SamplesTableTime,
    SamplesTableTimeSkeleton,
} from './components/SamplesTablePlayer/SamplesTableTime';

const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
};

interface SamplesTableRowContentProps {
    sample: Sample;
    page?: number;
    eventSubscription?: TableEventSubscriptionTypes;
    isLoading: boolean;
    rowRef: React.RefObject<HTMLTableRowElement>;
    columnsToHide?: { [key in SamplesTableColumns]: boolean };
    setIsRowFocused: React.Dispatch<React.SetStateAction<boolean>>;
    actionsToHide?: { [key in SamplesTableActions]: boolean };
    isSm: boolean;
    isMd: boolean;
    isMl: boolean;
    isLg: boolean;
    rowIndex: number | undefined;
    isRowActive: boolean;
}

const _SamplesTableRowContent = React.memo(
    ({
        rowIndex,
        rowRef,
        sample,
        page,
        eventSubscription,
        isLoading,
        actionsToHide,
        columnsToHide,
        setIsRowFocused,
        isSm,
        isMd,
        isMl,
        isLg,
        isRowActive,
    }: SamplesTableRowContentProps) => {
        const hasControlledHandler = !!eventSubscription;

        return (
            <>
                {hasControlledHandler && (
                    <SamplesTableRowObserver
                        eventSubscription={eventSubscription}
                        rowIndex={rowIndex}
                        rowRef={rowRef}
                        setIsRowFocused={setIsRowFocused}
                    />
                )}
                {!columnsToHide?.checkbox && (
                    <SamplesCell width="sm" isHidden={isSm}>
                        <SamplesTableCheckbox
                            sample={sample}
                            onClick={stopPropagation}
                        />
                    </SamplesCell>
                )}

                <SamplesCell width="sm" isHidden={isSm}>
                    <SamplesTablePackIcon
                        packSlug={sample.pack?.slug}
                        packName={sample.pack?.name}
                        packArtworkUrl={sample.pack?.artworkUrl}
                        onClick={stopPropagation}
                    />
                </SamplesCell>
                <SamplesCell className={playButtonCell} isHidden={isSm}>
                    <SamplesTablePlayButton
                        page={page}
                        rowIndex={rowIndex}
                        sampleGuid={sample.guid}
                        sampleWaveformUrl={sample.waveformUrl}
                        isLoading={isLoading}
                        rowRef={rowRef}
                        interactWithPlayer={!hasControlledHandler}
                    />
                </SamplesCell>
                <SamplesCell className={nameCell}>
                    <SamplesTableName
                        sampleGuid={sample.guid}
                        packArtworkUrl={sample.pack?.artworkUrl ?? ''}
                        packName={sample.pack?.name ?? ''}
                        sampleName={sample.name ?? ''}
                        sampleClassifiers={sample.classifiers ?? []}
                        page={page}
                        rowIndex={rowIndex}
                        withPlayer={isSm}
                    />
                </SamplesCell>
                <SamplesCell className={waveformCell} isHidden={isMl}>
                    <SamplesTableWaveform
                        page={page}
                        rowIndex={rowIndex}
                        sampleGuid={sample.guid}
                        sampleWaveformUrl={sample.waveformUrl}
                        isLoading={isLoading}
                        rowRef={rowRef}
                    />
                </SamplesCell>
                <SamplesCell
                    className={informationsCell({ isLargeDesktop: !isLg })}
                    isHidden={isMd}
                >
                    <SamplesTableTime sampleDuration={sample.duration} />
                </SamplesCell>
                <SamplesCell
                    className={informationsCell({ isLargeDesktop: !isLg })}
                    isHidden={isMd}
                >
                    <SamplesTableTypeIcon sampleType={sample.sampleType} />
                </SamplesCell>
                <SamplesCell
                    className={informationsCell({ isLargeDesktop: !isLg })}
                    isHidden={isMd}
                >
                    <SamplesTableBpm bpm={sample.bpm} />
                </SamplesCell>
                <SamplesCell
                    className={informationsCell({
                        isLargeDesktop: !isLg,
                        isKey: true,
                    })}
                    isHidden={isMd}
                >
                    <SamplesTableKey keyCode={sample.key?.code} />
                </SamplesCell>
                <SamplesCell
                    className={actionsGroupCellStyles({ isLg, isMd, isSm })}
                >
                    <SamplesTableActionsGroup
                        sample={sample}
                        page={page}
                        rowIndex={rowIndex}
                        actionsToHide={actionsToHide}
                        isMd={isMd}
                        isRowActive={isRowActive}
                    />
                </SamplesCell>
            </>
        );
    },
);

export interface SamplesTableRowProps {
    sample?: Sample;
    isLoading: boolean;
    page?: number;
    rowIndex?: number;
    eventSubscription?: TableEventSubscriptionTypes;
    onClick?: (event: React.MouseEvent<HTMLDivElement>, index: number) => void;
    columnsToHide?: {
        [key in SamplesTableColumns]: boolean;
    };
    actionsToHide?: {
        [key in SamplesTableActions]: boolean;
    };
    isSm: boolean;
    isMd: boolean;
    isMl: boolean;
    isLg: boolean;
}

const useIsLazy = ({
    rowRef,
}: {
    rowRef: React.RefObject<HTMLTableRowElement>;
}) => {
    const isInViewport = useDetectTargetIsInsideViewport({ ref: rowRef });

    const { search } = useLocation();
    const urlSearchParams = new URLSearchParams(search);
    const autoPlay = urlSearchParams.get(AudioConfigParams.autoPlay);

    const isLastRowAutoplay = autoPlay === 'last';

    const isLazy = !isLastRowAutoplay && !isInViewport;

    return isLazy;
};

const SamplesTableRowSkeleton = ({
    columnsToHide,
    isSm,
    isMd,
    isMl,
    isLg,
}: {
    columnsToHide?: { [key in SamplesTableColumns]: boolean };
    isSm: boolean;
    isMd: boolean;
    isMl: boolean;
    isLg: boolean;
}) => (
    <>
        {!columnsToHide?.checkbox && (
            <SamplesCell width="xs" isHidden={isSm}>
                <SamplesTableCheckboxSkeleton />
            </SamplesCell>
        )}
        <SamplesCell width="sm" isHidden={isSm}>
            <SamplesTablePackIconSkeleton />
        </SamplesCell>

        <SamplesCell className={playButtonCell} isHidden={isSm}>
            <SamplesTablePlayButtonSkeleton />
        </SamplesCell>

        <SamplesCell className={nameCell}>
            <SamplesTableNameSkeleton />
        </SamplesCell>
        <SamplesCell className={waveformCell} isHidden={isMl}>
            <SamplesTableWaveformSkeleton />
        </SamplesCell>
        <SamplesCell
            className={informationsCell({
                isLargeDesktop: !isLg,
            })}
            isHidden={isMd}
        >
            <SamplesTableTimeSkeleton />
        </SamplesCell>
        <SamplesCell
            className={informationsCell({
                isLargeDesktop: !isLg,
            })}
            isHidden={isMd}
        >
            <SamplesTableTypeIconSkeleton />
        </SamplesCell>
        <SamplesCell
            className={informationsCell({
                isLargeDesktop: !isLg,
            })}
            isHidden={isMd}
        >
            <SamplesTableBpmSkeleton />
        </SamplesCell>
        <SamplesCell
            className={informationsCell({
                isLargeDesktop: !isLg,
                isKey: true,
            })}
            isHidden={isMd}
        >
            <SamplesTableKeySkeleton />
        </SamplesCell>
        <SamplesCell
            className={actionsGroupCellStyles({
                isLg,
                isMd,
                isSm,
            })}
        >
            <SamplesTableActionsGroupSkeletons isMd={isMd} />
        </SamplesCell>
    </>
);

export const SamplesTableRow: React.FC<SamplesTableRowProps> = React.memo(
    ({
        sample,
        isLoading,
        page,
        rowIndex,
        eventSubscription,
        columnsToHide,
        actionsToHide,
        isSm,
        isMd,
        isMl,
        isLg,
    }) => {
        const rowRef = useRef<HTMLTableRowElement>(null);
        const { isSampleSelected } = useSelectedSamplesContext();

        const isSelected = isSampleSelected(sample);
        const [isRowHover, setIsRowHover] = useState(false);
        const [isRowFocused, setIsRowFocused] = useState(false);

        const [isPlayingSample, setIsPlayingSample] = useState(false);

        const handleRowMouseEnter = useCallback(() => {
            setIsRowHover(true);
        }, []);

        const handleRowMouseLeave = useCallback(() => {
            setIsRowHover(false);
        }, []);

        const isLazy = useIsLazy({ rowRef });

        const showSkeleton = isLoading || isLazy;

        return (
            <Table.Row
                isSelected={isSelected || isPlayingSample}
                height="72px"
                rowIndex={rowIndex}
                data-isdownloaded={sample?.isDownloaded}
                ref={rowRef}
                hasHover
                hasFocus
                onMouseEnter={handleRowMouseEnter}
                onMouseLeave={handleRowMouseLeave}
                cursor="pointer"
            >
                {(showSkeleton || !sample) && (
                    <SamplesTableRowSkeleton
                        columnsToHide={columnsToHide}
                        isSm={isSm}
                        isMd={isMd}
                        isMl={isMl}
                        isLg={isLg}
                    />
                )}
                {!showSkeleton && sample && (
                    <PlayerProvider
                        playerId={sample.guid}
                        streamEntityType={StreamEntityType.Samples}
                        notifyIsPlaying={setIsPlayingSample}
                    >
                        <_SamplesTableRowContent
                            rowIndex={rowIndex}
                            rowRef={rowRef}
                            sample={sample}
                            page={page}
                            eventSubscription={eventSubscription}
                            isLoading={isLoading}
                            actionsToHide={actionsToHide}
                            columnsToHide={columnsToHide}
                            setIsRowFocused={setIsRowFocused}
                            isRowActive={isRowFocused || isRowHover}
                            isSm={isSm}
                            isMd={isMd}
                            isMl={isMl}
                            isLg={isLg}
                        />
                    </PlayerProvider>
                )}
            </Table.Row>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.sample?.guid === nextProps.sample?.guid &&
            prevProps.sample?.isDownloaded === nextProps.sample?.isDownloaded &&
            prevProps.isLoading === nextProps.isLoading &&
            prevProps.page === nextProps.page &&
            prevProps.rowIndex === nextProps.rowIndex &&
            prevProps.eventSubscription === nextProps.eventSubscription &&
            prevProps.isSm === nextProps.isSm &&
            prevProps.isMd === nextProps.isMd &&
            prevProps.isMl === nextProps.isMl &&
            prevProps.isLg === nextProps.isLg &&
            JSON.stringify(prevProps.columnsToHide) ===
                JSON.stringify(nextProps.columnsToHide) &&
            JSON.stringify(prevProps.actionsToHide) ===
                JSON.stringify(nextProps.actionsToHide)
        );
    },
);
